/**
 * useGameProgress Hook
 * 
 * Manages game progress state, persistence, and question history tracking.
 * Provides functions to save/load progress from storage and track which questions
 * have been asked to avoid repetition.
 * 
 * @module useGameProgress
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { storage, STORAGE_KEYS } from '../utils/storage';
import { ONE_WEEK_MS } from '../constants/timing';
import { progressSync } from '../utils/progressSync';
import { useUser } from '../contexts/UserContext';
import { logger } from '../utils/logger';
import { useOfflineQueue } from './useOfflineQueue';

/**
 * Hook for managing game progress, including scores, achievements, and question history.
 * 
 * @returns {Object} Progress state and management functions
 * @returns {Object} returns.progress - Current progress state object
 * @returns {Object} returns.progress.completedSubjects - Object mapping subject names to completion data
 * @returns {number} returns.progress.totalScore - Total points earned across all subjects
 * @returns {number} returns.progress.streak - Current learning streak count
 * @returns {string|null} returns.progress.lastPlayed - Last played timestamp or null
 * @returns {Array} returns.progress.achievements - Array of unlocked achievement IDs
 * @returns {Array} returns.progress.unlockedThemes - Array of available theme names
 * @returns {string} returns.progress.selectedTheme - Currently active theme name
 * @returns {string} returns.progress.avatar - Selected avatar identifier
 * @returns {Array} returns.progress.certificates - Array of earned certificates
 * @returns {Object} returns.progress.questionHistory - Nested object tracking question IDs by subject and timestamp
 * @returns {Object} returns.progress.stats - Statistics object with learning metrics
 * @returns {Function} returns.setProgress - Function to update progress state directly
 * @returns {Function} returns.saveProgress - Function to update and persist progress to storage
 * @returns {Function} returns.getQuestionId - Function to generate unique identifiers for questions
 * @returns {Function} returns.trackQuestionHistory - Function to record when a question was asked
 * @returns {Function} returns.cleanupQuestionHistory - Function to remove old question history entries
 * 
 * @example
 * const { progress, saveProgress, trackQuestionHistory } = useGameProgress();
 * 
 * // Track that a question was asked
 * trackQuestionHistory('math', 'addition-1');
 * 
 * // Update progress after completing a subject
 * saveProgress({
 *   ...progress,
 *   totalScore: progress.totalScore + 100,
 *   completedSubjects: {
 *     ...progress.completedSubjects,
 *     math: { score: 100, completed: true }
 *   }
 * });
 */
export const useGameProgress = () => {
  const { user, userProfile } = useUser();
  const { queueUpdate, isOnline } = useOfflineQueue();
  
  // Storage and logger are now statically imported
  
  // Initialize progress from storage (lazy load to avoid initialization issues)
  const [progress, setProgress] = useState(() => {
    // Use localStorage directly for initial state to avoid module import
    try {
      const saved = localStorage.getItem(`emmy-${STORAGE_KEYS.PROGRESS}`);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {
          // Invalid JSON, use default
        }
      }
    } catch (e) {
      // Silently fail
    }
    return {
      completedSubjects: {},
      totalScore: 0,
      streak: 0,
      lastPlayed: null,
      achievements: [],
      unlockedThemes: ['default'],
      selectedTheme: 'default',
      avatar: 'default',
      certificates: [],
      questionHistory: {}, // Track previously asked questions by subject
      stats: {
        totalQuestionsAnswered: 0,
        correctAnswers: 0,
        perfectScores: 0,
        timeSpent: 0,
        favoriteSubject: null
      }
    };
  });

  // Load progress from database on mount if user is authenticated
  useEffect(() => {
    const loadFromDB = async () => {
      // Safety check: ensure user exists and has id before accessing properties
      if (!user || !user.id || user.is_guest) return;
      
      // Check if user is a demo/test user (not a real authenticated Supabase user)
      const isDemoUser = user.is_demo || 
        user.is_fallback || 
        user.is_developer ||
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(user.id);
      
      if (isDemoUser) {
        logger.debug('Skipping database load for demo/test user');
        return;
      }
      
      // Use progressSync which has proper error handling and demo user checks
      const dbProgress = await progressSync.loadProgressFromDB(user);
      
      if (dbProgress) {
        // Get local progress
        const localProgress = storage.get(STORAGE_KEYS.PROGRESS) || progress;
        
        // Resolve conflicts and merge
        const mergedProgress = progressSync.resolveConflict(localProgress, dbProgress);
        
        // Update local state and storage
        setProgress(mergedProgress);
        storage.set(STORAGE_KEYS.PROGRESS, mergedProgress);
        logger.debug('Progress loaded from database and merged with local');
      }
    };

    loadFromDB();
  }, [user]); // Only run when user changes

  /**
   * Saves progress state to both React state and persistent storage.
   * Also syncs to database if user is authenticated.
   * 
   * @param {Object} newProgress - The new progress object to save
   * @param {Object} [newProgress.completedSubjects] - Updated completed subjects map
   * @param {number} [newProgress.totalScore] - Updated total score
   * @param {Object} [newProgress.questionHistory] - Updated question history
   * @param {boolean} [immediate] - Whether to sync immediately (default: debounced)
   * @returns {Promise<void>}
   * 
   * @example
   * saveProgress({
   *   ...progress,
   *   totalScore: progress.totalScore + 50,
   *   completedSubjects: { ...progress.completedSubjects, math: { score: 50 } }
   * });
   */
  const saveProgress = useCallback(async (newProgress, immediate = false) => {
    // Update local state and storage immediately
    logger.debug('[useGameProgress] saveProgress called, setting new progress with avatar:', newProgress?.avatar);
    setProgress(newProgress);
    storage.set(STORAGE_KEYS.PROGRESS, newProgress);

    // Sync to database if user is authenticated
    if (user && !user.is_guest) {
      if (isOnline) {
        // Online: sync directly
        try {
          await progressSync.syncProgress(newProgress, user, immediate);
        } catch (error) {
          // If sync fails while online, queue it for later
          logger.warn('Progress sync failed, queuing for later:', error);
          queueUpdate({
            type: 'progress',
            data: newProgress,
            priority: 1
          });
        }
      } else {
        // Offline: queue the update
        queueUpdate({
          type: 'progress',
          data: newProgress,
          priority: 1
        });
        logger.debug('Progress queued for offline sync');
      }
    }
  }, [user, isOnline, queueUpdate]);

  /**
   * Generates a unique identifier for a question object.
   * Used to track which questions have been asked to avoid repetition.
   * 
   * @param {Object} question - Question object
   * @param {string} [question.word] - Word property (for phonics/spelling questions)
   * @param {string} [question.question] - Question text
   * @param {string} [question.answer] - Answer text
   * @returns {string} Unique identifier for the question
   * 
   * @example
   * const id = getQuestionId({ word: 'cat', question: 'What is this?' });
   * // Returns: 'cat-What is this?'
   */
  const getQuestionId = useCallback((question) => {
    if (question.word) {
      return `${question.word}-${question.question}`;
    }
    return question.question || question.answer || JSON.stringify(question);
  }, []);

  /**
   * Records that a question was asked for a specific subject.
   * This helps avoid showing the same questions too frequently.
   * 
   * @param {string} subject - Subject identifier (e.g., 'math', 'phonics')
   * @param {string} questionId - Unique identifier for the question
   * @returns {void}
   * 
   * @example
   * trackQuestionHistory('math', 'addition-5+3');
   */
  const trackQuestionHistory = useCallback((subject, questionId) => {
    const newProgress = {
      ...progress,
      questionHistory: {
        ...(progress.questionHistory || {}),
        [subject]: {
          ...(progress.questionHistory?.[subject] || {}),
          [questionId]: Date.now()
        }
      }
    };
    saveProgress(newProgress);
  }, [progress, saveProgress]);

  /**
   * Removes question history entries older than one week to prevent storage bloat.
   * Automatically called on mount. Questions asked within the last week are preserved.
   * 
   * @returns {void}
   */
  const cleanupQuestionHistory = useCallback(() => {
    const currentTime = Date.now();
    const oneWeek = ONE_WEEK_MS;
    const cleanedHistory = {};
    
    // Check if questionHistory exists and is an object
    if (!progress.questionHistory || typeof progress.questionHistory !== 'object') {
      return;
    }
    
    Object.keys(progress.questionHistory).forEach(subject => {
      const subjectHistory = progress.questionHistory[subject];
      if (!subjectHistory || typeof subjectHistory !== 'object') {
        return;
      }
      
      const cleanedSubject = {};
      
      Object.keys(subjectHistory).forEach(questionId => {
        const lastAsked = subjectHistory[questionId];
        // Keep questions asked within the last week
        if (lastAsked && currentTime - lastAsked < oneWeek) {
          cleanedSubject[questionId] = lastAsked;
        }
      });
      
      // Only keep subjects that have questions
      if (Object.keys(cleanedSubject).length > 0) {
        cleanedHistory[subject] = cleanedSubject;
      }
    });
    
    if (JSON.stringify(cleanedHistory) !== JSON.stringify(progress.questionHistory)) {
      const newProgress = {
        ...progress,
        questionHistory: cleanedHistory
      };
      saveProgress(newProgress);
    }
  }, [progress.questionHistory, saveProgress]);

  // Clean up question history on mount
  useEffect(() => {
    if (progress && progress.questionHistory) {
      cleanupQuestionHistory();
    }
  }, []); // Only run on mount

  return {
    progress,
    setProgress,
    saveProgress,
    getQuestionId,
    trackQuestionHistory,
    cleanupQuestionHistory
  };
};

